import type { TemperatureBand } from "@ui/utils/temperature-bands";
import { useCallback, useMemo } from "react";
import { type SearchSettings, useSearchkitClient } from "@/hooks/use-searchkit-client";
import { DESTINATION_FACETS } from "./facets";

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

// Hoisted so `useSearchkitClient`'s memo deps stay reference-stable across
// renders. Per-page Searchkit config never depends on hook state.
const SEARCH_SETTINGS: SearchSettings = {
  // Searchkit reads `insideBoundingBox` from InstantSearch UI state (set by the
  // SearchBar Where popover and the map pan handler) and emits an ES
  // `geo_bounding_box` filter against this field.
  geo_attribute: "location",
  // Target Searchkick's `.word_middle` subfield — it indexes every substring
  // of every word for typo/prefix-friendly matching.
  search_attributes: [{ field: "name.word_middle", weight: 3 }],
  result_attributes: [
    "id",
    "name",
    "slug",
    "country_slug",
    "country_code",
    "marketing_sentence",
    "cover_image",
    "photos",
    "cost_tier",
    "cost_total_cents",
    "cost_currency",
    "cost_evidence_grade",
    "cost_coverage",
    "location",
    ...MONTHS.map((m) => `avg_temp_${m}`),
  ],
  highlight_attributes: [],
  facet_attributes: [
    ...MONTHS.map((m) => {
      const field = DESTINATION_FACETS.tempBand(m);
      return { attribute: field, field, type: "string" as const };
    }),
    { attribute: DESTINATION_FACETS.utcOffset, field: DESTINATION_FACETS.utcOffset, type: "string" as const },
    { attribute: DESTINATION_FACETS.koppen, field: DESTINATION_FACETS.koppen, type: "string" as const },
    { attribute: DESTINATION_FACETS.languages, field: DESTINATION_FACETS.languages, type: "string" as const },
    { attribute: "cost_total_cents", field: "cost_total_cents", type: "numeric" as const },
  ],
  // Filter-only: the Where pill narrows by these as terms and neither needs facet
  // counts, so they stay out of `facet_attributes`. An attribute missing here makes
  // Searchkit throw on the facetFilter rather than degrade — keep in step with the
  // `facetFilters` SearchMapSection passes to <Configure>.
  filter_attributes: [
    { attribute: "region", field: "region", type: "string" as const },
    { attribute: "country_code", field: "country_code", type: "string" as const },
  ],
  sorting: {
    default: { field: "_score", order: "desc" },
    _price_asc: { field: "cost_total_cents", order: "asc" },
    _price_desc: { field: "cost_total_cents", order: "desc" },
  },
};

interface UseDestinationsSearchOptions {
  budget: number | null;
  effectiveMonths: number[];
  selectedBands: TemperatureBand[];
}

// The destinations search front door
// (`Destinations::UseCases::RankDestinationSearch`) reads this block, builds a
// `Destinations::Operations::Scoring::Preferences`, and strips it before
// forwarding to Elasticsearch. The cluster retrieves; the blend that orders
// the results runs in Ruby on the way back. Field names and the key match
// those parsed in `preferences.rb`.
export const SCORING_PAYLOAD_KEY = "_travily_scoring";

export function useDestinationsSearch({ budget, effectiveMonths, selectedBands }: UseDestinationsSearchOptions) {
  // An omitted budget is not the same as a budget of nothing: the ranking
  // anchors the affordability threshold on the corpus median when none is sent,
  // and a zero would put every destination over it.
  const scoring = useMemo(
    () => ({
      effective_months: effectiveMonths,
      selected_bands: selectedBands,
      ...(budget === null ? {} : { budget_cents: budget }),
    }),
    [budget, effectiveMonths, selectedBands],
  );

  // The Searchkit adapter only forwards known InstantSearch params into the ES
  // body, so the scoring block is injected here at the transporter layer.
  const requestEnhancer = useCallback(
    (body: Record<string, unknown>) => ({ ...body, [SCORING_PAYLOAD_KEY]: scoring }),
    [scoring],
  );

  const client = useSearchkitClient({
    host: "/api/destinations",
    indexName: "destinations",
    searchSettings: SEARCH_SETTINGS,
    requestEnhancer,
    sessionKey: "destinations",
  });

  // `scoring` rides out as well as in: the map plots its own geo aggregation
  // through a bare fetch that never reaches the transporter, so it has to be
  // handed the same block or the pins describe a set the list does not.
  return { ...client, scoring };
}
